import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  advancePlaygroundPhysics,
  ballTransformOffset,
  blastAt,
  brickBodyIdAt,
  flickBrick,
  getExistingPlaygroundRuntime,
  getPlaygroundRuntime,
  resetPlayground,
  throwBall,
  BALL_RADIUS,
  BRICK_FULL,
  MAX_BALLS,
  MAX_BRICKS,
  type PlaygroundRuntime,
  type PlaygroundTool,
} from '../physics/playgroundPhysics';
import './PhysicsPlayground.css';

interface PlaygroundState {
  canvasReady: boolean;
  physicsReady: boolean;
  bodyCount: number;
  awakeCount: number;
  tool: PlaygroundTool;
}

interface PlaygroundActions {
  flickAt: (ndcX: number, ndcY: number) => void;
  throwBall: (ndcX: number, ndcY: number) => void;
  blastAt: (ndcX: number, ndcY: number) => void;
  reset: () => void;
}

interface PlaygroundDebug {
  stepMs: number;
  brickSample: Array<{ x: number; y: number; z: number }>;
  caps: Record<string, boolean>;
}

declare global {
  interface Window {
    __playgroundState?: PlaygroundState;
    __playgroundActions?: PlaygroundActions;
    __playgroundDebug?: PlaygroundDebug;
  }
}

function writePlaygroundState(patch: Partial<PlaygroundState>) {
  const previous = window.__playgroundState;
  window.__playgroundState = {
    canvasReady: patch.canvasReady ?? previous?.canvasReady ?? false,
    physicsReady: patch.physicsReady ?? previous?.physicsReady ?? false,
    bodyCount: patch.bodyCount ?? previous?.bodyCount ?? 0,
    awakeCount: patch.awakeCount ?? previous?.awakeCount ?? 0,
    tool: patch.tool ?? previous?.tool ?? 'flick',
  };
}

interface PlaygroundStats {
  bodies: number;
  awake: number;
  stepMs: number;
  fps: number;
}

interface PlaygroundFx {
  impact: (x: number, y: number, z: number, strength: number) => void;
  blast: (x: number, y: number, z: number) => void;
}

// ---------- shared textures ----------

function makeGlowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(64, 64, 2, 64, 64, 64);
    grad.addColorStop(0, 'rgba(255, 236, 190, 1)');
    grad.addColorStop(0.28, 'rgba(255, 200, 110, 0.55)');
    grad.addColorStop(0.65, 'rgba(255, 160, 70, 0.12)');
    grad.addColorStop(1, 'rgba(255, 150, 60, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 128, 128);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeGroundTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#101318';
    ctx.fillRect(0, 0, 256, 256);
    // Large tonal blotches so the lit pool doesn't read as a flat gradient
    // (QA round 1: ground looked uniform vs the yard's mottled asphalt).
    for (let i = 0; i < 10; i += 1) {
      const bx = Math.random() * 256;
      const by = Math.random() * 256;
      const br = 28 + Math.random() * 46;
      const light = Math.random() < 0.45;
      const grad = ctx.createRadialGradient(bx, by, 2, bx, by, br);
      grad.addColorStop(0, light ? 'rgba(190, 200, 220, 0.07)' : 'rgba(0, 0, 0, 0.3)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(bx - br, by - br, br * 2, br * 2);
    }
    for (let i = 0; i < 220; i += 1) {
      const x = Math.random() * 256;
      const y = Math.random() * 256;
      const w = 2 + Math.random() * 22;
      const h = 1 + Math.random() * 3;
      const v = Math.random();
      ctx.fillStyle = v < 0.45 ? 'rgba(190, 200, 220, 0.08)' : 'rgba(0, 0, 0, 0.3)';
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.random() * Math.PI);
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    }
    // paving seams — heavier than round 1 so they survive the warm light pool
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.lineWidth = 4;
    for (let p = 0; p <= 256; p += 64) {
      ctx.beginPath();
      ctx.moveTo(p, 0);
      ctx.lineTo(p, 256);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, p);
      ctx.lineTo(256, p);
      ctx.stroke();
    }
    // faint highlight along each seam edge to sell the bevel
    ctx.strokeStyle = 'rgba(190, 200, 220, 0.06)';
    ctx.lineWidth = 1;
    for (let p = 0; p <= 256; p += 64) {
      ctx.beginPath();
      ctx.moveTo(p + 3, 0);
      ctx.lineTo(p + 3, 256);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, p + 3);
      ctx.lineTo(256, p + 3);
      ctx.stroke();
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(7, 7);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function seedRand(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

// ---------- scene dressing ----------

function Ground() {
  const texture = useMemo(() => makeGroundTexture(), []);
  useEffect(() => () => texture.dispose(), [texture]);

  const patches = useMemo(() => {
    const list: Array<{ x: number; z: number; w: number; d: number; dark: boolean; rot: number }> = [];
    for (let i = 0; i < 60; i += 1) {
      const a = seedRand(i * 5 + 3) * Math.PI * 2;
      // Kept outside the painted arena (r < ~7.5) so slabs never cross markings.
      const r = 8 + seedRand(i * 7 + 1) * 18;
      list.push({
        x: Math.cos(a) * r,
        z: Math.sin(a) * r,
        w: 1.2 + seedRand(i * 11 + 2) * 2.2,
        d: 1.2 + seedRand(i * 13 + 4) * 2.2,
        dark: seedRand(i * 17 + 5) < 0.5,
        rot: seedRand(i * 19 + 6) * 0.4 - 0.2,
      });
    }
    return list;
  }, []);

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[130, 130]} />
        <meshStandardMaterial map={texture} color="#c6ccd8" roughness={0.95} metalness={0.02} />
      </mesh>
      {patches.map((patch, i) => (
        <mesh key={i} position={[patch.x, 0.025, patch.z]} rotation={[0, patch.rot, 0]} receiveShadow>
          <boxGeometry args={[patch.w, 0.05, patch.d]} />
          <meshStandardMaterial color={patch.dark ? '#14161c' : '#191c23'} roughness={0.96} />
        </mesh>
      ))}
      <GroundMarkings />
    </group>
  );
}

// Faint painted markings around the formations — a worn arena border with
// corner pads and a ring under the pyramid. Visual only, no physics.
// (QA round 2 dropped the hazard dashes: one ended abruptly mid-frame and read
// as a glitch rather than paint.)
function GroundMarkings() {
  const paint = { color: '#b7a878', transparent: true, roughness: 0.92, metalness: 0 } as const;
  // Arena border rectangle enclosing all three formations
  // (wall x -6.3..-3.1 / pyramid z up to 2.0 / tower x up to 3.8, z -2.0).
  const x0 = -7.0;
  const x1 = 4.6;
  const z0 = -2.9;
  const z1 = 2.9;
  const w = x1 - x0;
  const d = z1 - z0;
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  const t = 0.14; // stripe width
  const y = 0.015;
  return (
    <group>
      {/* border stripes */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, y, z0]}>
        <planeGeometry args={[w, t]} />
        <meshStandardMaterial {...paint} opacity={0.3} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, y, z1]}>
        <planeGeometry args={[w, t]} />
        <meshStandardMaterial {...paint} opacity={0.3} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x0, y, cz]}>
        <planeGeometry args={[t, d]} />
        <meshStandardMaterial {...paint} opacity={0.3} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x1, y, cz]}>
        <planeGeometry args={[t, d]} />
        <meshStandardMaterial {...paint} opacity={0.3} />
      </mesh>
      {/* corner pads so every stripe terminates deliberately */}
      {[
        [x0, z0],
        [x0, z1],
        [x1, z0],
        [x1, z1],
      ].map(([px, pz], i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[px, y + 0.001, pz]}>
          <planeGeometry args={[0.42, 0.42]} />
          <meshStandardMaterial {...paint} opacity={0.34} />
        </mesh>
      ))}
      {/* painted ring under the pyramid */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.3, y, 0.7]}>
        <ringGeometry args={[1.95, 2.08, 48]} />
        <meshStandardMaterial {...paint} opacity={0.24} />
      </mesh>
    </group>
  );
}

function Stars() {
  const positions = useMemo(() => {
    const count = 320;
    const array = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      const a = seedRand(i * 2 + 1) * Math.PI * 2;
      const elevation = 0.1 + seedRand(i * 3 + 2) * 0.85;
      const r = 90;
      array[i * 3] = Math.cos(a) * r * Math.cos(elevation);
      array[i * 3 + 1] = Math.sin(elevation) * r;
      array[i * 3 + 2] = Math.sin(a) * r * Math.cos(elevation);
    }
    return array;
  }, []);
  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        color="#cfd8ef"
        size={1.6}
        transparent
        opacity={0.55}
        depthWrite={false}
        sizeAttenuation={false}
        fog={false}
      />
    </points>
  );
}

function VoxelClouds() {
  const clusters: Array<{ pos: [number, number, number]; slabs: Array<[number, number, number, number, number, number]> }> = [
    {
      pos: [-20, 20, -24],
      slabs: [
        [0, 0, 0, 15, 1.5, 6.5],
        [5, 1.3, 2, 9, 1.3, 4.5],
      ],
    },
    {
      pos: [17, 23, -18],
      slabs: [
        [0, 0, 0, 13, 1.5, 5.5],
        [-4, 1.2, 2.5, 7, 1.2, 3.6],
      ],
    },
    {
      pos: [2, 25, 17],
      slabs: [
        [0, 0, 0, 16, 1.7, 6.5],
        [7, -1.3, -2, 8, 1.3, 4],
      ],
    },
  ];
  return (
    <group>
      {clusters.map((cluster, ci) => (
        <group key={ci} position={cluster.pos}>
          {cluster.slabs.map(([x, y, z, w, h, d], si) => (
            <mesh key={si} position={[x, y, z]}>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color="#10141f" emissive="#1a1208" emissiveIntensity={0.22} roughness={1} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function Lighting() {
  const spotTargetA = useMemo(() => {
    const target = new THREE.Object3D();
    target.position.set(0, 1.4, 0);
    return target;
  }, []);
  const spotTargetB = useMemo(() => {
    const target = new THREE.Object3D();
    target.position.set(0.4, 1.0, -0.6);
    return target;
  }, []);

  return (
    <>
      {/* Warm dark ground bounce + warm ambient: shadowed brick faces must
          read as dark terracotta, not crushed navy-black (QA round 2). */}
      <hemisphereLight args={['#2a3f74', '#241812', 0.75]} />
      <ambientLight intensity={0.09} color="#54402c" />
      <primitive object={spotTargetA} />
      <primitive object={spotTargetB} />
      <spotLight
        position={[-9, 8.4, 4.2]}
        target={spotTargetA}
        color="#ffb54d"
        intensity={1400}
        angle={0.6}
        penumbra={0.6}
        distance={40}
        decay={1.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0002}
        shadow-camera-near={2}
        shadow-camera-far={40}
      />
      <spotLight
        position={[7.5, 7.6, -5]}
        target={spotTargetB}
        color="#ffab42"
        intensity={950}
        angle={0.6}
        penumbra={0.6}
        distance={38}
        decay={1.8}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-bias={-0.0002}
        shadow-camera-near={2}
        shadow-camera-far={38}
      />
      <directionalLight position={[13, 8, -11]} intensity={0.35} color="#5a78c8" />
      {/* Very low warm fill from the camera side (no shadow casting — cheap)
          so camera-facing shadow sides keep their palette identity. */}
      <directionalLight position={[7, 4.5, 8.5]} intensity={0.25} color="#c08050" />
    </>
  );
}

// ---------- effects ----------

const PUFF_POOL = 24;
const EMBER_COUNT = 120;

interface PuffState {
  active: boolean;
  life: number;
  maxLife: number;
  strength: number;
}

function Effects({
  fxRef,
  shakeRef,
  glowTexture,
}: {
  fxRef: MutableRefObject<PlaygroundFx | null>;
  shakeRef: MutableRefObject<number>;
  glowTexture: THREE.Texture;
}) {
  const puffRefs = useRef<Array<THREE.Sprite | null>>([]);
  const puffStates = useRef<PuffState[]>(
    Array.from({ length: PUFF_POOL }, () => ({ active: false, life: 0, maxLife: 0.5, strength: 1 })),
  );
  const flashRef = useRef<THREE.PointLight | null>(null);
  const ringRef = useRef<THREE.Mesh | null>(null);
  const embersRef = useRef<THREE.Points | null>(null);
  const blastState = useRef({ active: false, life: 0, x: 0, y: 0, z: 0 });
  const emberData = useRef({
    positions: new Float32Array(EMBER_COUNT * 3),
    velocities: new Float32Array(EMBER_COUNT * 3),
  });

  useEffect(() => {
    fxRef.current = {
      impact(x, y, z, strength) {
        const states = puffStates.current;
        const index = states.findIndex((state) => !state.active);
        if (index < 0) return;
        const sprite = puffRefs.current[index];
        if (!sprite) return;
        states[index] = { active: true, life: 0, maxLife: 0.5, strength: Math.min(1, strength / 10) };
        sprite.position.set(x, y, z);
        sprite.visible = true;
        shakeRef.current = Math.min(0.4, shakeRef.current + Math.min(0.1, strength * 0.008));
      },
      blast(x, y, z) {
        blastState.current = { active: true, life: 0, x, y, z };
        const { positions, velocities } = emberData.current;
        for (let i = 0; i < EMBER_COUNT; i += 1) {
          positions[i * 3] = x + (Math.random() - 0.5) * 0.6;
          positions[i * 3 + 1] = y + (Math.random() - 0.5) * 0.6;
          positions[i * 3 + 2] = z + (Math.random() - 0.5) * 0.6;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);
          const speed = 2.5 + Math.random() * 6;
          velocities[i * 3] = Math.sin(phi) * Math.cos(theta) * speed;
          velocities[i * 3 + 1] = Math.abs(Math.cos(phi)) * speed + 2.2;
          velocities[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * speed;
        }
        const points = embersRef.current;
        if (points) {
          const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
          (attr.array as Float32Array).set(positions);
          attr.needsUpdate = true;
        }
        shakeRef.current = Math.min(0.6, shakeRef.current + 0.32);
      },
    };
    return () => {
      fxRef.current = null;
    };
  }, [fxRef, shakeRef]);

  useFrame((_, delta) => {
    const states = puffStates.current;
    for (let i = 0; i < states.length; i += 1) {
      const state = states[i];
      const sprite = puffRefs.current[i];
      if (!state.active || !sprite) continue;
      state.life += delta;
      const t = state.life / state.maxLife;
      if (t >= 1) {
        state.active = false;
        sprite.visible = false;
        continue;
      }
      const scale = 0.6 + t * 2.0;
      sprite.scale.set(scale, scale, 1);
      const material = sprite.material as THREE.SpriteMaterial;
      material.opacity = 0.5 * (1 - t) * (0.4 + state.strength * 0.6);
      sprite.position.y += delta * 0.5;
    }

    const blast = blastState.current;
    const flash = flashRef.current;
    const ring = ringRef.current;
    const points = embersRef.current;
    if (blast.active) {
      blast.life += delta;
      const t = blast.life;
      if (flash) {
        flash.position.set(blast.x, blast.y, blast.z);
        flash.intensity = t < 0.3 ? 320 * (1 - t / 0.3) : 0;
      }
      if (ring) {
        ring.visible = t < 0.55;
        ring.position.set(blast.x, Math.max(0.1, blast.y - 0.3), blast.z);
        const scale = 1 + t * 24;
        ring.scale.set(scale, scale, scale);
        (ring.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.75 * (1 - t / 0.55));
      }
      if (points) {
        points.visible = t < 1;
        const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
        const array = attr.array as Float32Array;
        const { velocities } = emberData.current;
        for (let i = 0; i < EMBER_COUNT; i += 1) {
          velocities[i * 3 + 1] -= 7.5 * delta;
          array[i * 3] += velocities[i * 3] * delta;
          array[i * 3 + 1] += velocities[i * 3 + 1] * delta;
          array[i * 3 + 2] += velocities[i * 3 + 2] * delta;
        }
        attr.needsUpdate = true;
        (points.material as THREE.PointsMaterial).opacity = Math.max(0, 1 - t);
      }
      if (t >= 1) {
        blast.active = false;
        if (flash) flash.intensity = 0;
        if (ring) ring.visible = false;
        if (points) points.visible = false;
      }
    }
  });

  return (
    <group>
      {Array.from({ length: PUFF_POOL }, (_, i) => (
        <sprite
          key={`puff-${i}`}
          ref={(sprite) => {
            puffRefs.current[i] = sprite;
          }}
          visible={false}
        >
          <spriteMaterial
            map={glowTexture}
            color="#ffca8a"
            transparent
            opacity={0}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </sprite>
      ))}
      <pointLight ref={flashRef} intensity={0} distance={16} decay={2} color="#ffb54d" />
      <mesh ref={ringRef} visible={false} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.44, 0.52, 48]} />
        <meshBasicMaterial
          color="#ffc27a"
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <points ref={embersRef} visible={false}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[new Float32Array(EMBER_COUNT * 3), 3]} />
        </bufferGeometry>
        <pointsMaterial
          color="#ff9a3d"
          size={0.09}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          sizeAttenuation
          toneMapped={false}
        />
      </points>
    </group>
  );
}

function ShakeRig({ shakeRef, children }: { shakeRef: MutableRefObject<number>; children: React.ReactNode }) {
  const groupRef = useRef<THREE.Group | null>(null);
  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;
    const shake = shakeRef.current;
    if (shake > 0.0006) {
      group.position.set(
        (Math.random() - 0.5) * shake * 0.35,
        (Math.random() - 0.5) * shake * 0.2,
        (Math.random() - 0.5) * shake * 0.35,
      );
      shakeRef.current = shake * Math.exp(-delta * 5.5);
    } else if (group.position.lengthSq() > 0) {
      group.position.set(0, 0, 0);
    }
  });
  return <group ref={groupRef}>{children}</group>;
}

// ---------- physics render layer ----------

function PlaygroundPhysicsLayer({
  toolRef,
  resetToken,
  statsRef,
  fxRef,
  shakeRef,
  actionsRef,
  onReady,
}: {
  toolRef: MutableRefObject<PlaygroundTool>;
  resetToken: number;
  statsRef: MutableRefObject<PlaygroundStats>;
  fxRef: MutableRefObject<PlaygroundFx | null>;
  shakeRef: MutableRefObject<number>;
  actionsRef: MutableRefObject<PlaygroundActions | null>;
  onReady: () => void;
}) {
  const { gl, camera } = useThree();
  const runtimeRef = useRef<PlaygroundRuntime | null>(getExistingPlaygroundRuntime());
  const brickMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const ballMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const colorsVersionRef = useRef(-1);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pointerNdc = useMemo(() => new THREE.Vector2(), []);
  const planeHit = useMemo(() => new THREE.Vector3(), []);
  const groundPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  // FPS warmup: skip the first few (loading-spiky) frames, then SEED the EMA
  // with the first real sample instead of averaging up from 0 — QA round 1
  // caught the panel showing "3" on load while true FPS was 60.
  const fpsWarmupRef = useRef(0);

  // runtime bootstrap
  useEffect(() => {
    let cancelled = false;
    getPlaygroundRuntime()
      .then((runtime) => {
        if (cancelled) return;
        runtimeRef.current = runtime;
        writePlaygroundState({ physicsReady: true });
        onReady();
      })
      .catch((error: unknown) => {
        console.error('Playground physics failed to initialize', error);
        writePlaygroundState({ physicsReady: false });
      });
    return () => {
      cancelled = true;
    };
  }, [onReady]);

  const lastResetToken = useRef(resetToken);
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime && resetToken !== lastResetToken.current) {
      lastResetToken.current = resetToken;
      resetPlayground(runtime);
    }
  }, [resetToken]);

  // Core interaction drivers, keyed on NDC coordinates so both pointer input and
  // the DEV automation bridge (__playgroundActions) run identical code paths.
  const setRaycaster = useCallback(
    (ndcX: number, ndcY: number) => {
      pointerNdc.set(ndcX, ndcY);
      raycaster.setFromCamera(pointerNdc, camera);
    },
    [camera, pointerNdc, raycaster],
  );

  const doFlick = useCallback(
    (ndcX: number, ndcY: number) => {
      const runtime = runtimeRef.current;
      const brickMesh = brickMeshRef.current;
      if (!runtime || !brickMesh) return;
      setRaycaster(ndcX, ndcY);
      const hits = raycaster.intersectObject(brickMesh, false);
      const instanceId = hits[0]?.instanceId;
      if (instanceId == null) return;
      const bodyId = brickBodyIdAt(runtime, instanceId);
      if (!bodyId) return;
      const dir = raycaster.ray.direction;
      flickBrick(runtime, instanceId, { x: dir.x, y: dir.y, z: dir.z });
      const hit = hits[0].point;
      shakeRef.current = Math.min(0.5, shakeRef.current + 0.05);
      fxRef.current?.impact(hit.x, hit.y, hit.z, 6);
    },
    [fxRef, raycaster, setRaycaster, shakeRef],
  );

  const doThrowBall = useCallback(
    (ndcX: number, ndcY: number) => {
      const runtime = runtimeRef.current;
      if (!runtime) return;
      setRaycaster(ndcX, ndcY);
      const dir = raycaster.ray.direction;
      const origin = camera.position.clone().add(dir.clone().multiplyScalar(1.4));
      throwBall(runtime, { x: origin.x, y: origin.y, z: origin.z }, { x: dir.x, y: dir.y, z: dir.z });
      shakeRef.current = Math.min(0.5, shakeRef.current + 0.05);
    },
    [camera, raycaster, setRaycaster, shakeRef],
  );

  const doBlast = useCallback(
    (ndcX: number, ndcY: number) => {
      const runtime = runtimeRef.current;
      const brickMesh = brickMeshRef.current;
      if (!runtime) return;
      setRaycaster(ndcX, ndcY);
      let point: THREE.Vector3 | null = null;
      if (brickMesh) {
        const hits = raycaster.intersectObject(brickMesh, false);
        if (hits.length > 0) point = hits[0].point.clone();
      }
      if (!point && raycaster.ray.intersectPlane(groundPlane, planeHit)) {
        point = planeHit.clone();
      }
      if (!point) return;
      point.x = Math.min(Math.max(point.x, -30), 30);
      point.z = Math.min(Math.max(point.z, -30), 30);
      blastAt(runtime, point.x, point.y, point.z);
      fxRef.current?.blast(point.x, point.y, point.z);
    },
    [fxRef, groundPlane, planeHit, raycaster, setRaycaster],
  );

  // pointer input → NDC → same drivers
  useEffect(() => {
    const canvas = gl.domElement;
    const toNdc = (event: PointerEvent): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      return [
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      ];
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const [nx, ny] = toNdc(event);
      const tool = toolRef.current;
      if (tool === 'flick') doFlick(nx, ny);
      else if (tool === 'ball') doThrowBall(nx, ny);
      else if (tool === 'blast') doBlast(nx, ny);
    };
    canvas.addEventListener('pointerdown', onPointerDown);
    return () => canvas.removeEventListener('pointerdown', onPointerDown);
  }, [doBlast, doFlick, doThrowBall, gl, toolRef]);

  // DEV automation bridge
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const actions: PlaygroundActions = {
      flickAt: doFlick,
      throwBall: doThrowBall,
      blastAt: doBlast,
      reset: () => {
        const runtime = runtimeRef.current;
        if (runtime) resetPlayground(runtime);
      },
    };
    actionsRef.current = actions;
    window.__playgroundActions = actions;
    return () => {
      actionsRef.current = null;
      delete window.__playgroundActions;
    };
  }, [actionsRef, doBlast, doFlick, doThrowBall]);

  useFrame((_, delta) => {
    const runtime = runtimeRef.current;
    writePlaygroundState({ canvasReady: true });
    if (!runtime) return;

    advancePlaygroundPhysics(runtime, delta);

    const stats = statsRef.current;
    stats.bodies = runtime.bodyCountTotal;
    stats.awake = runtime.awakeCount;
    stats.stepMs = stats.stepMs * 0.85 + runtime.stepMs * 0.15;
    if (delta > 0) {
      fpsWarmupRef.current += 1;
      if (fpsWarmupRef.current > 5) {
        const inst = 1 / delta;
        stats.fps = stats.fps <= 0 ? inst : stats.fps * 0.92 + inst * 0.08;
      }
    }

    writePlaygroundState({
      bodyCount: runtime.bodyCountTotal,
      awakeCount: runtime.awakeCount,
    });

    // impacts → dust puffs
    if (runtime.impacts.length > 0) {
      const fx = fxRef.current;
      if (fx) {
        for (const impact of runtime.impacts) {
          fx.impact(impact.x, impact.y, impact.z, impact.strength);
        }
      }
      runtime.impacts.length = 0;
    }

    const brickMesh = brickMeshRef.current;
    const ballMesh = ballMeshRef.current;

    if (brickMesh) {
      if (colorsVersionRef.current !== runtime.structureVersion) {
        colorsVersionRef.current = runtime.structureVersion;
        brickMesh.count = runtime.brickBodies.length;
        // Static generous bounding sphere so click raycasts always pass the
        // broadphase wherever bricks have flown (see WreckingYard note).
        brickMesh.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 3, 0), 90);
        for (let i = 0; i < runtime.brickBodies.length; i += 1) {
          color.set(runtime.brickColors[i]);
          const jitterSeed = Math.sin(i * 12.9898) * 43758.5453;
          const jitter = jitterSeed - Math.floor(jitterSeed);
          color.offsetHSL(0, (jitter - 0.5) * 0.05, (jitter - 0.5) * 0.13);
          brickMesh.setColorAt(i, color);
        }
        if (brickMesh.instanceColor) brickMesh.instanceColor.needsUpdate = true;
      }

      for (let i = 0; i < runtime.brickBodies.length; i += 1) {
        const offset = i * 7;
        dummy.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
        dummy.quaternion.set(
          runtime.transforms[offset + 3],
          runtime.transforms[offset + 4],
          runtime.transforms[offset + 5],
          runtime.transforms[offset + 6],
        );
        dummy.scale.setScalar(1);
        dummy.updateMatrix();
        brickMesh.setMatrixAt(i, dummy.matrix);
      }
      brickMesh.instanceMatrix.needsUpdate = true;
    }

    if (ballMesh) {
      ballMesh.count = MAX_BALLS;
      for (let i = 0; i < MAX_BALLS; i += 1) {
        if (i < runtime.balls.length) {
          const offset = ballTransformOffset(runtime, i);
          dummy.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
          dummy.quaternion.set(
            runtime.transforms[offset + 3],
            runtime.transforms[offset + 4],
            runtime.transforms[offset + 5],
            runtime.transforms[offset + 6],
          );
          dummy.scale.setScalar(1);
        } else {
          dummy.position.set(0, -120, 0);
          dummy.quaternion.identity();
          dummy.scale.setScalar(0.001);
        }
        dummy.updateMatrix();
        ballMesh.setMatrixAt(i, dummy.matrix);
      }
      ballMesh.instanceMatrix.needsUpdate = true;
    }

    if (import.meta.env.DEV) {
      const brickSample: Array<{ x: number; y: number; z: number }> = [];
      for (let i = 0; i < Math.min(6, runtime.brickBodies.length); i += 1) {
        const offset = i * 7;
        brickSample.push({
          x: runtime.transforms[offset],
          y: runtime.transforms[offset + 1],
          z: runtime.transforms[offset + 2],
        });
      }
      window.__playgroundDebug = {
        stepMs: runtime.stepMs,
        brickSample,
        caps: { ...runtime.caps },
      };
    }
  });

  return (
    <group>
      <instancedMesh
        ref={brickMeshRef}
        args={[undefined, undefined, MAX_BRICKS]}
        count={0}
        castShadow
        receiveShadow
        frustumCulled={false}
      >
        <boxGeometry args={[BRICK_FULL.x, BRICK_FULL.y, BRICK_FULL.z]} />
        <meshStandardMaterial roughness={0.8} metalness={0.04} />
      </instancedMesh>
      <instancedMesh ref={ballMeshRef} args={[undefined, undefined, MAX_BALLS]} castShadow frustumCulled={false}>
        <icosahedronGeometry args={[BALL_RADIUS, 1]} />
        <meshStandardMaterial color="#3a3f46" metalness={0.6} roughness={0.5} flatShading />
      </instancedMesh>
    </group>
  );
}

function PlaygroundCamera({ controlsRef }: { controlsRef: MutableRefObject<OrbitControlsImpl | null> }) {
  // A gentle intro drift: ease the orbit azimuth from wherever the camera
  // actually starts (no hardcoded angle → no first-frame snap), cancelled the
  // moment the user grabs the controls.
  const introRef = useRef<{ start: number; azimuth: number; cancelled: boolean } | null>(null);
  useFrame((state) => {
    const controls = controlsRef.current;
    if (!controls) return;
    let intro = introRef.current;
    if (!intro) {
      intro = { start: state.clock.elapsedTime, azimuth: controls.getAzimuthalAngle(), cancelled: false };
      introRef.current = intro;
      controls.addEventListener('start', () => {
        if (introRef.current) introRef.current.cancelled = true;
      });
    }
    if (intro.cancelled) return;
    const t = state.clock.elapsedTime - intro.start;
    if (t < 2.6) {
      const eased = 1 - Math.pow(1 - t / 2.6, 3);
      controls.setAzimuthalAngle(intro.azimuth - eased * 0.16);
    }
  });
  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.08}
      target={[0.2, 1.3, -0.3]}
      minDistance={4.5}
      maxDistance={22}
      maxPolarAngle={Math.PI * 0.49}
    />
  );
}

// ---------- HUD ----------

type Cell = [number, number];

function row(y: number, x0: number, x1: number): Cell[] {
  const cells: Cell[] = [];
  for (let x = x0; x <= x1; x += 1) cells.push([x, y]);
  return cells;
}

// Flick: a pointing hand / finger flicking a block.
const ICON_FLICK: Cell[] = [
  [8, 1], [9, 1], [8, 2], [9, 2],
  [7, 3], [8, 3],
  [6, 4], [7, 4],
  [5, 5], [6, 5],
  [4, 6], [5, 6],
  ...row(7, 2, 4),
  ...row(8, 1, 4),
  ...row(9, 1, 4),
  [1, 8], [1, 9],
];

// Ball: filled circle.
const ICON_BALL: Cell[] = [
  ...row(2, 4, 7),
  ...row(3, 3, 8),
  ...row(4, 2, 9),
  ...row(5, 2, 9),
  ...row(6, 2, 9),
  ...row(7, 2, 9),
  ...row(8, 3, 8),
  ...row(9, 4, 7),
].filter(([x, y]) => !(x === 4 && y === 4) && !(x === 7 && y === 7));

// Blast: radial burst.
const ICON_BLAST: Cell[] = [
  [5, 5], [6, 5], [5, 6], [6, 6],
  [5, 1], [6, 1], [5, 2], [6, 2],
  [5, 9], [6, 9], [5, 10], [6, 10],
  [1, 5], [1, 6], [2, 5], [2, 6],
  [9, 5], [9, 6], [10, 5], [10, 6],
  [3, 3], [8, 3], [3, 8], [8, 8],
  [2, 2], [9, 2], [2, 9], [9, 9],
];

const ICON_RESET: Cell[] = [
  ...row(2, 4, 7),
  [3, 3], [8, 3],
  [2, 4], [9, 4],
  [2, 5], [9, 5],
  [2, 6], [9, 6],
  [3, 7], [8, 7],
  ...row(8, 4, 7),
  [10, 2], [10, 3], [9, 2], [8, 1],
];

function PixelIcon({ cells }: { cells: Cell[] }) {
  return (
    <svg className="pp-icon" viewBox="0 0 12 12" aria-hidden="true" shapeRendering="crispEdges">
      {cells.map(([x, y], i) => (
        <rect key={i} x={x} y={y} width={1} height={1} fill="currentColor" />
      ))}
    </svg>
  );
}

const TOOLS: Array<{ id: PlaygroundTool; label: string; icon: Cell[] }> = [
  { id: 'flick', label: 'Flick', icon: ICON_FLICK },
  { id: 'ball', label: 'Ball', icon: ICON_BALL },
  { id: 'blast', label: 'Blast', icon: ICON_BLAST },
];

function StatBlock({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="pp-stat">
      <span className="pp-stat-label">{label}</span>
      <span className="pp-stat-value">
        {value}
        {suffix && <em className="pp-stat-suffix">{suffix}</em>}
      </span>
    </div>
  );
}

function PhysicsPlayground({ onBack }: { onBack: () => void }) {
  const [tool, setTool] = useState<PlaygroundTool>('flick');
  const [resetToken, setResetToken] = useState(0);
  const [physicsReady, setPhysicsReady] = useState(false);
  const [stats, setStats] = useState<PlaygroundStats>({ bodies: 0, awake: 0, stepMs: 0, fps: 0 });

  const statsRef = useRef<PlaygroundStats>({ bodies: 0, awake: 0, stepMs: 0, fps: 0 });
  const fxRef = useRef<PlaygroundFx | null>(null);
  const shakeRef = useRef(0);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const actionsRef = useRef<PlaygroundActions | null>(null);
  const toolRef = useRef<PlaygroundTool>('flick');

  const selectTool = useCallback((next: PlaygroundTool) => {
    // Sync the ref synchronously so a canvas click landing before the next
    // React render still uses the freshly selected tool.
    toolRef.current = next;
    setTool(next);
  }, []);

  const glowTexture = useMemo(() => makeGlowTexture(), []);
  useEffect(() => () => glowTexture.dispose(), [glowTexture]);

  useEffect(() => {
    writePlaygroundState({ tool });
  }, [tool]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStats({ ...statsRef.current });
    }, 250);
    return () => window.clearInterval(interval);
  }, []);

  const handleReady = useCallback(() => {
    setPhysicsReady(true);
  }, []);

  const cursor = tool === 'flick' ? 'pointer' : 'crosshair';

  return (
    <div className="physics-playground-page">
      <Canvas
        shadows
        camera={{ position: [5.6, 3.6, 6.6], fov: 46 }}
        gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.15 }}
        style={{ cursor }}
      >
        <color attach="background" args={['#060a16']} />
        <fogExp2 attach="fog" args={['#060a16', 0.014]} />
        <Stars />
        <VoxelClouds />
        <PlaygroundCamera controlsRef={controlsRef} />
        <ShakeRig shakeRef={shakeRef}>
          <Lighting />
          <Ground />
          <PlaygroundPhysicsLayer
            toolRef={toolRef}
            resetToken={resetToken}
            statsRef={statsRef}
            fxRef={fxRef}
            shakeRef={shakeRef}
            actionsRef={actionsRef}
            onReady={handleReady}
          />
          <Effects fxRef={fxRef} shakeRef={shakeRef} glowTexture={glowTexture} />
        </ShakeRig>
      </Canvas>

      <div className="pp-overlay">
        <header className="pp-brand">
          <button className="pp-exit" type="button" onClick={onBack}>
            Exit
          </button>
          <h1 className="pp-title">Physics Playground</h1>
          <div className="pp-title-rule" />
        </header>

        <aside className="pp-stats" data-testid="playground-stats">
          <StatBlock label="Bodies" value={stats.bodies.toLocaleString('en-US')} />
          <StatBlock label="Awake" value={stats.awake.toLocaleString('en-US')} />
          <StatBlock label="Step" value={stats.stepMs.toFixed(1)} suffix="ms" />
          <StatBlock label="FPS" value={stats.fps > 0 ? Math.round(stats.fps).toString() : '—'} />
        </aside>

        <nav className="pp-tools">
          {TOOLS.map(({ id, label, icon }) => (
            <button
              key={id}
              className={`pp-tool ${tool === id ? 'active' : ''}`}
              data-testid={`tool-${id}`}
              type="button"
              onClick={() => selectTool(id)}
            >
              <PixelIcon cells={icon} />
              <span className="pp-seg-label">{label}</span>
            </button>
          ))}
        </nav>

        <div className="pp-side">
          <button
            className="pp-reset"
            data-testid="reset-playground"
            type="button"
            aria-label="Reset playground"
            onClick={() => setResetToken((token) => token + 1)}
          >
            <PixelIcon cells={ICON_RESET} />
          </button>
        </div>

        {!physicsReady && <div className="pp-loading">Stacking the bricks…</div>}
      </div>
    </div>
  );
}

export default PhysicsPlayground;
